import { PosSync } from '@/types'

import { db, meta, PULL_ONLY_TABLES, PUSH_TABLES, rows, TableName } from '@/db/database'

import { configService } from '@/services/ConfigService'
import { SentryService } from '@/services/SentryService'

import { AuthExpiredError, BusinessInactiveError, DeviceUnlinkedError, MaintenanceError, streamRequest } from './stream'

const BASE = '/api/backoffice/pos-offline'
const CURSOR_KEY = 'sync.cursor'
const LAST_SYNCED_KEY = 'sync.last_synced_at'
const EXPIRATION_KEY = 'business.expiration_date'
const INTERVAL_MS = 30_000
const ALL_TABLES: TableName[] = [...PULL_ONLY_TABLES, ...PUSH_TABLES]
// Fields that are the device's own bookkeeping, never sent to the server.
const LOCAL_FIELDS = ['id', 'synced', 'issue_fields']

export interface SyncState {
  status: PosSync.Status
  pending: number
  lastSyncedAt: string | null
  /** True only during the first pull of a new device, the one time a full-screen progress is allowed (U3). */
  initialising: boolean
  progress: number
  error: string | null
  expirationDate: string | null
}

type Listener = (state: SyncState) => void

/**
 * Moves rows between the device database and the server (POS.md §15.9). Screens never call it to get data; they
 * read the local tables. Writes call `requestSync()`; the engine also runs on an interval and when the network
 * comes back. One cycle at a time: pull, push what the server does not have, pull again (rule 1).
 */
class SyncEngine {
  private state: SyncState = {
    status: 'idle',
    pending: 0,
    lastSyncedAt: null,
    initialising: false,
    progress: 0,
    error: null,
    expirationDate: null,
  }
  private listeners = new Set<Listener>()
  private running: Promise<void> | null = null
  private again = false
  private timer: ReturnType<typeof setInterval> | null = null
  private branchId: string | null = null
  /** Called when the server refuses the token, so the session can drop it. */
  onAuthExpired: (() => void) | null = null
  /** Called when the server refuses this device (unlinked); the session asks to link again. Local data stays. */
  onDeviceUnlinked: (() => void) | null = null

  getState = () => this.state

  subscribe = (listener: Listener) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private set(patch: Partial<SyncState>) {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((listener) => listener(this.state))
  }

  /** Starts background sync for a set-up device. */
  async start(branchId: string) {
    this.branchId = branchId
    this.set({
      lastSyncedAt: (await meta.get<string>(LAST_SYNCED_KEY)) ?? null,
      expirationDate: (await meta.get<string>(EXPIRATION_KEY)) ?? null,
      pending: await this.countPending(),
    })
    window.addEventListener('online', this.requestSync)
    this.timer = setInterval(this.requestSync, INTERVAL_MS)
    this.requestSync()
  }

  stop() {
    window.removeEventListener('online', this.requestSync)
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    this.branchId = null
  }

  /** The first pull of a new device: the caller shows its progress; afterwards sync runs in the background. */
  async initialise(branchId: string) {
    this.branchId = branchId
    this.set({ initialising: true, progress: 0, error: null })
    try {
      await this.pull()
    } finally {
      this.set({ initialising: false })
    }
  }

  /** Schedules a cycle; a request during a cycle runs one more cycle after it, never two at once. */
  requestSync = () => {
    if (this.running) {
      this.again = true
      return
    }
    this.running = this.cycle().finally(() => {
      this.running = null
      if (this.again) {
        this.again = false
        this.requestSync()
      }
    })
  }

  private async cycle() {
    if (!this.branchId) return
    if (!navigator.onLine) {
      this.set({ status: 'offline' })
      return
    }
    if (!configService.getConfig().apiConfig.bearerToken) {
      this.set({ status: 'auth', pending: await this.countPending() })
      return
    }
    this.set({ status: 'syncing', error: null })
    try {
      await this.pull()
      if (await this.push()) await this.pull()
      const now = new Date().toISOString()
      await meta.set(LAST_SYNCED_KEY, now)
      this.set({
        status: 'idle',
        lastSyncedAt: now,
        pending: await this.countPending(),
      })
    } catch (error) {
      if (error instanceof DeviceUnlinkedError) {
        this.set({ status: 'auth', error: error.message, pending: await this.countPending() })
        this.onDeviceUnlinked?.()
        return
      }
      if (error instanceof BusinessInactiveError) {
        this.set({ status: 'suspended', error: error.message, pending: await this.countPending() })
        return
      }
      if (error instanceof MaintenanceError) {
        this.set({ status: 'maintenance', error: error.message, pending: await this.countPending() })
        return
      }
      if (error instanceof AuthExpiredError) {
        this.set({ status: 'auth', error: null, pending: await this.countPending() })
        this.onAuthExpired?.()
        return
      }
      console.error('[pos sync]', error)
      // TypeError = network failure, not a fault.
      if (!(error instanceof TypeError)) SentryService.captureException(error, { where: 'sync' })
      this.set({
        status: navigator.onLine ? 'error' : 'offline',
        error: (error as Error).message,
      })
    }
  }

  /**
   * The device was connected for another branch (POS.md §18.2): push what this branch's terminal still has waiting,
   * then forget the old branch's data so the next set-up pulls the new one from scratch. Push only: a pull would
   * already bring the new branch. True when done; false while offline, signed out or records are still waiting.
   */
  async leaveBranch(): Promise<boolean> {
    if (!navigator.onLine || !configService.getConfig().apiConfig.bearerToken) return false
    await this.running
    this.set({ pending: await this.countPending() })
    if (this.state.pending > 0) await this.push()
    this.set({ pending: await this.countPending() })
    if (this.state.pending > 0) return false
    await this.wipeSynced()
    await meta.set(CURSOR_KEY, undefined)
    return true
  }

  /** Records written on this device that the server has not confirmed yet. */
  async countPending() {
    let pending = 0
    for (const table of PUSH_TABLES) pending += await rows(table).where('synced').equals(0).count()
    return pending
  }

  private async pull() {
    const cursor = await meta.get<string>(CURSOR_KEY)
    let failure: string | null = null
    await streamRequest(
      `${BASE}/data`,
      { method: 'GET', query: { branch_id: this.branchId!, cursor } },
      async (event) => {
        if (event.name === 'progress') this.set({ progress: event.data.percent })
        else if (event.name === 'reset') await this.wipeSynced()
        else if (event.name === 'data') await this.applyPulled(event.data.type, event.data.items)
        else if (event.name === 'complete') {
          await meta.set(CURSOR_KEY, event.data.cursor)
          if ('expiration_date' in event.data) {
            await meta.set(EXPIRATION_KEY, event.data.expiration_date)
            this.set({ expirationDate: event.data.expiration_date })
          }
        }
        else if (event.name === 'error') failure = event.data.message
      }
    )
    if (failure) throw new Error(failure)
  }

  /** A `reset` pull starts from nothing: synced rows go, rows waiting to push stay (rule 13). */
  private async wipeSynced() {
    await db.transaction('rw', ALL_TABLES.map(rows), async () => {
      for (const table of ALL_TABLES) await rows(table).where('synced').equals(1).delete()
    })
  }

  private async applyPulled(type: string, items: any[]) {
    if (type === 'tombstones') return this.applyTombstones(items)
    if (!ALL_TABLES.includes(type as TableName)) return
    const table = rows(type as TableName)
    await db.transaction('rw', table, async () => {
      for (const item of items) {
        const local =
          (item._id && (await table.where('_id').equals(item._id).first())) ||
          (item.temp_id && (await table.where('temp_id').equals(item.temp_id).first()))
        // Never overwrite a row the server has not confirmed yet (rule 9).
        if (local && local.synced !== 1) continue
        await table.put({ ...item, id: local?.id ?? item._id, synced: 1 })
      }
    })
  }

  private async applyTombstones(items: PosSync.Tombstone[]) {
    for (const tombstone of items) {
      if (!ALL_TABLES.includes(tombstone.entity as TableName)) continue
      await rows(tombstone.entity as TableName)
        .where('_id')
        .equals(tombstone.record_id)
        .and((row) => row.synced === 1)
        .delete()
    }
  }

  /** Sends every row waiting to push in one request. Returns whether anything was sent. */
  private async push(): Promise<boolean> {
    const payload: Record<string, any[]> = {}
    for (const table of PUSH_TABLES) {
      const waiting = await rows(table).where('synced').equals(0).toArray()
      if (waiting.length > 0) {
        payload[table] = waiting.map((row) =>
          Object.fromEntries(Object.entries(row).filter(([key]) => !LOCAL_FIELDS.includes(key)))
        )
      }
    }
    if (Object.keys(payload).length === 0) return false

    let results: Record<string, PosSync.RecordResult[]> = {}
    let failure: string | null = null
    await streamRequest(`${BASE}/sync`, { method: 'POST', body: payload }, (event) => {
      if (event.name === 'complete') results = event.data.results ?? {}
      if (event.name === 'error') {
        results = event.data.results ?? {}
        failure = event.data.message
      }
    })
    // Keys the server finished are recorded even when a later key failed; the rest stay pending and go again.
    await this.applyResults(results)
    if (failure) throw new Error(failure)
    return true
  }

  private async applyResults(results: Record<string, PosSync.RecordResult[]>) {
    for (const [key, list] of Object.entries(results)) {
      if (!PUSH_TABLES.includes(key as any)) continue
      const table = rows(key as TableName)
      await db.transaction('rw', table, async () => {
        for (const result of list) {
          const local =
            (result.temp_id && (await table.where('temp_id').equals(result.temp_id).first())) ||
            (result._id && (await table.where('_id').equals(result._id).first()))
          if (!local) continue
          if (result.status === 'rejected') {
            await table.update(local.id, {
              synced: -1,
              issue_fields: result.issue_fields ?? [],
            })
          } else {
            // A customer merged into an existing one (same phone) may already be here as a pulled row: keep one.
            if (result._id) {
              await table
                .where('_id')
                .equals(result._id)
                .and((row) => row.id !== local.id && row.synced === 1)
                .delete()
            }
            await table.update(local.id, {
              _id: result._id ?? undefined,
              synced: 1,
              issue_fields: undefined,
            })
          }
        }
      })
    }
  }
}

export const syncEngine = new SyncEngine()
